import * as React from "react";

import {
  AllowedJobTypeList,
  isExtensionPrompt,
  isMicrosoftOrigin,
  isTenantPrompt,
  isUserPrompt,
  isWorkgroupPrompt,
  type ExtensionPrompt,
  type Group,
  type Metadata,
  type Prompt,
} from "@1js/pl-types";
import { Avatar } from "@fluentui/react-components";
import { JobTypeWeights, type JobType } from "../data";
import {
  CreatedByKey,
  DisplayCategoryKey,
  ExtensionKey,
  JobTypeKey,
  LikedKey,
  ProductNameKey,
  WorkgroupKey,
} from "../PromptLibrary/PromptLibrary.keys";
import { PromptTabs } from "../PromptTab/PromptTabs";
import type {
  Filter,
  FilterName,
  FilterOption,
  NestedFilter,
  OrganizationType,
  Submenu,
} from "./types";

type ExtensionPartyType = {
  Origin: string;
  ProviderPartyType: string;
  ExtensionLoggableId?: string;
};
/**
 * Group filter options by group key.
 */
export const groupFilters = (filter: Filter) => {
  return groupFilterOptions(filter.options);
};

export const groupFilterOptions = (filterOptions: FilterOption[]) => {
  return filterOptions.reduce(
    (
      acc: {
        [group: string]: {
          value: string;
          group?: string;
        }[];
      },
      curr: {
        value: string;
        group?: string;
      }
    ) => {
      const group = curr.group ?? "NA";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(curr);
      return acc;
    },
    {}
  );
};

const convertMetadataToJobTypeFilters = (metadatas: Metadata[]) => {
  const uniqueJobTypeFilterTracker = new Set<string>();
  let metadataFilters: FilterOption[] = [];
  metadatas.forEach((item: Metadata) => {
    if (AllowedJobTypeList.has(item.Name)) {
      const key = `${item.Name}_${item.Value}`;
      if (!uniqueJobTypeFilterTracker.has(key)) {
        uniqueJobTypeFilterTracker.add(key);
        metadataFilters.push({
          value: item.Value,
          group: item.Name,
        });
      }
    }
  });

  metadataFilters = metadataFilters.sort((a, b) => {
    const aWeight = JobTypeWeights[a.group as JobType] || 0;
    const bWeight = JobTypeWeights[b.group as JobType] || 0;
    return aWeight - bWeight;
  });

  return metadataFilters;
};

/**
 * Populates missing fields for extension prompts. The reason for postprocessing is that 3s sends extension prompts without these fields
 * @param prompts list of prompts
 * @returns prompts with extension prompts having their missing fields populated
 */
export const populateMissingFieldsForPrompts = (
  prompts: Prompt[]
): Prompt[] => {
  return prompts.map((prompt) => {
    // TODO: Daniel, this defaulting logic is breaking user expectation for user-authored prompts. As a temp fix I am only scoping your logic to agent prompts
    // please work with PM to see what's the right defaulting logic that can be applied for all non-msft prompts
    if (isExtensionPrompt(prompt)) {
      if (!prompt.DisplayCategory) {
        prompt = {
          ...prompt,
          DisplayCategory: "Ask",
        };
      }
      if (!prompt.Products) {
        prompt = {
          ...prompt,
          Products: ["BizChat"],
        };
      }
    }
    return prompt;
  });
};

/**
 * Utility function to construct nested options for submenu filters
 * @param options the existing options for the whole filter, will be grouped
 * @returns Array of nested filters, where the displayLabel is the group key and options are the filter options for that group
 */
export const constructNestedOptions = (
  options: FilterOption[]
): NestedFilter[] => {
  const groupedOptions = groupFilterOptions(options);
  const nestedOptions = Object.keys(groupedOptions).map((groupKey) => {
    return {
      displayLabel: groupKey,
      options: groupedOptions[groupKey],
    };
  });
  return nestedOptions;
};

export const buildFilters = (
  prompts: Prompt[],
  excludes?: FilterName[],
  extensionIdMap?: Record<string, string>,
  isRocksteadyUxEnabled?: boolean,
  isMetaOsV2?: boolean
): Filter[] => {
  if (!prompts || prompts.length === 0) {
    return [];
  }
  prompts = populateMissingFieldsForPrompts(prompts);
  const displayCategories: string[] = [];
  let productNames: string[] = [];
  let microsoftMetadata: Metadata[] = [];
  let tenantMetadata: Metadata[] = [];

  let extensions: Record<string, ExtensionPartyType> = {};

  prompts.forEach((p) => {
    if (p.Origin !== "User" && p.DisplayCategory) {
      displayCategories.push(p.DisplayCategory.toString());
    }
    if (p.Products) {
      productNames = [...productNames, ...p.Products];
    }

    if (p.Metadata) {
      if (isTenantPrompt(p)) {
        tenantMetadata = [...tenantMetadata, ...p.Metadata];
      } else {
        microsoftMetadata = [...microsoftMetadata, ...p.Metadata];
      }
    }

    if (p.Origin && p.ProviderName && isExtensionPrompt(p)) {
      const extensionPrompt = p as ExtensionPrompt;
      const extensionId = extensionPrompt.ProviderId
        ? extensionIdMap?.[extensionPrompt.ProviderId]
        : undefined;

      extensions[p.ProviderName] = {
        Origin: p.Origin,
        ProviderPartyType: (p as ExtensionPrompt).ProviderPartyType as string,
        ExtensionLoggableId: extensionId,
      };
    }
  });

  extensions = postProcessExtensionFilters(extensions, prompts);

  // TESTING PURPOSES ONLY REMOVE LATER:
  // tenantMetadata = [{ Name: "Department", Value: "TEST FILTER OPTION VALUE" }];

  const filters = [
    {
      key: ProductNameKey,
      options: [...new Set(productNames)].map((x) => {
        return { value: x };
      }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: LikedKey,
      options: [{ value: "Liked" }, { value: "NotLiked" }],
      isSingleSelect: true,
      hidden: true,
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: DisplayCategoryKey,
      options: [...new Set(displayCategories)]
        .filter((category) => category !== "Agent" && category !== "Tenant")
        .map((x) => {
          return { value: x };
        }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    },
    {
      key: JobTypeKey,
      options: convertMetadataToJobTypeFilters([
        ...microsoftMetadata,
        ...tenantMetadata,
      ]),
      type: isRocksteadyUxEnabled || isMetaOsV2 ? "nested" : "dropdown",
      isLogValuesInTelemetry: true,
      ...(isRocksteadyUxEnabled || isMetaOsV2
        ? {
            submenuGroups: [
              {
                groupLabel: "FromMicrosoft" as OrganizationType,
                nestedOptions: constructNestedOptions(
                  convertMetadataToJobTypeFilters(microsoftMetadata)
                ) as NestedFilter[],
              },
              ...(tenantMetadata.length > 0
                ? [
                    {
                      groupLabel: "FromOrganization" as OrganizationType,
                      nestedOptions: constructNestedOptions(
                        convertMetadataToJobTypeFilters(tenantMetadata)
                      ) as NestedFilter[],
                    },
                  ]
                : []),
            ] as Submenu[],
          }
        : {}),
    },
    {
      key: ExtensionKey,
      options: [...new Set(Object.keys(extensions))]
        .map((x) => {
          return { value: x };
        })
        .sort((a, b) => (a.value > b.value ? 1 : -1)),
      type: "dropdown",
      isLogValuesInTelemetry: false,
      telemetryValueOverrides: createTelemetryValueMapForExtensions(extensions),
    },
  ] as Filter[];

  if (excludes && excludes.length > 0) {
    return filters.filter((filter) => !excludes.includes(filter.key));
  }

  return filters;
};

export const createWorkgroupFilterFromPrompts = (
  groups: Group[],
  prompts: Prompt[]
): Filter => {
  const workgroupIds = new Set<string>();
  prompts.forEach((prompt) => {
    if (prompt.Groups) {
      prompt.Groups.forEach((group) => {
        workgroupIds.add(group.Id);
      });
    }
  });

  const workgroupFilter: Filter = {
    key: WorkgroupKey,
    options: groups
      .filter((group) => workgroupIds.has(group.Id))
      .map((group) => {
        const image = group.AvatarUrl ? { src: group.AvatarUrl } : undefined;

        return {
          value: group.Id,
          displayLabel: group.DisplayName,
          badge: (
            <Avatar
              name={group.DisplayName}
              shape="square"
              image={image}
              color="colorful"
              size={20}
            />
          ),
        };
      }),
    type: "combobox",
    isSingleSelect: false,
    isOverflowItemsFrom10: true,
    isLogValuesInTelemetry: false,
    width: "wide",
  };

  return workgroupFilter;
};

type BuildFilterProps = {
  prompts: Prompt[];
  excludes?: FilterName[];
  extensionIdMap?: Record<string, string>;
  teams?: Group[];
  disabledFilters?: FilterName[];
  excludeSingleOptions?: boolean;
};

export const buildAllFilters = (props: BuildFilterProps): Filter[] => {
  const {
    prompts,
    excludes,
    extensionIdMap,
    teams,
    disabledFilters,
    excludeSingleOptions,
  } = props;
  let filters = buildFilters(prompts, excludes, extensionIdMap, true);

  if (teams && excludes && !excludes?.includes(WorkgroupKey)) {
    const workgroupFilter = createWorkgroupFilterFromPrompts(teams, prompts);
    filters = filters.concat(workgroupFilter);
  }

  if (excludes && !excludes?.includes(CreatedByKey)) {
    filters = (
      [
        {
          key: CreatedByKey,
          isSingleSelect: true,
          options: [
            ...(prompts.some(isTenantPrompt)
              ? [{ value: "CreatedByTenant" }]
              : []),
            ...(prompts.some(isWorkgroupPrompt)
              ? [{ value: "CreatedByOthers" }]
              : []),
            ...(prompts.some(isUserPrompt) ? [{ value: "CreatedByMe" }] : []),
            ...(prompts.some(isMicrosoftOrigin)
              ? [{ value: "CreatedByMicrosoft" }]
              : []),
          ],
          type: "dropdown",
        },
      ] as Filter[]
    ).concat(filters);
  }

  if (disabledFilters) {
    filters = filters.map((filter) => {
      if (disabledFilters.includes(filter.key)) {
        return {
          ...filter,
          disabled: true,
        };
      }
      return filter;
    });
  }

  if (excludeSingleOptions) {
    filters = filters.filter((filter) => {
      return filter.key === CreatedByKey || filter.options.length > 1;
    });
  }

  return filters;
};

/**
 * Filters out extensions if they are present in all prompts
 * @param extensions Record of extension name and associated extension and party type
 * @param prompts list of prompts
 * @returns Filtered record of extension name and associated extension and party type
 */
const postProcessExtensionFilters = (
  extensions: Record<string, ExtensionPartyType>,
  prompts: Prompt[]
): Record<string, ExtensionPartyType> => {
  const filteredExtensions: Record<string, ExtensionPartyType> = {};

  Object.entries(extensions).forEach(([extensionName, extensionPartyType]) => {
    if (!prompts.every((prompt) => prompt.ProviderName === extensionName)) {
      filteredExtensions[extensionName] = extensionPartyType;
    }
  });

  return filteredExtensions;
};

/**
 * Generates lookup map matching provider name to generated string that will be logged in telemetry for extensions since extension names are considered customer data
 * @param extensions Record of extension name and associated extension and party type
 * @returns Lookup table with provider name as key and generic values for telemetry as values (e.g. Data_ExtensionPlugIn_1p, Data_ExtensionPlugIn_3p, Data_ExtensionDC_1p, Data_ExtensionDC_3p)
 */
const createTelemetryValueMapForExtensions = (
  extensions: Record<string, ExtensionPartyType>
): Record<string, string> => {
  return Object.entries(extensions).reduce(
    (result: Record<string, string>, [key, value]) => {
      result[key] = generateExtensionTelemetryValue(value);
      return result;
    },
    {}
  );
};

/**
 * Generates string from origin and party type that will be logged in telemetry for extensions inplace of extension name since it is considered customer data
 * @param extensionString extension name
 * @param partyString party type
 * @returns string for telemetry (e.g. Data_ExtensionPlugIn_1p, Data_ExtensionPlugIn_3p, Data_ExtensionDC_1p, Data_ExtensionDC_3p)
 */
export const generateExtensionTelemetryValue = (
  extensionPartyType: ExtensionPartyType
): string => {
  // TODO: Remove logging extensionType and partyType in telemetry once we are good with titleId logging.
  if (extensionPartyType.ExtensionLoggableId) {
    return extensionPartyType.ExtensionLoggableId;
  }
  const extensionType = getExtensionType(extensionPartyType.Origin as string);
  const partyType = getPartyType(
    extensionPartyType.ProviderPartyType as string
  );
  return `Data_Extension${extensionType}_${partyType}`;
};

/**
 * Generates string from origin
 * @param originString
 * @returns string for extension type (e.g. PlugIn, DC, User)
 */
const getExtensionType = (originString: string): string => {
  switch (originString) {
    case "Plugin":
      return "PlugIn";
    case "GPT":
      return "DC";
    case "User":
      return "User";
    default:
      return "Unknown";
  }
};

/**
 * Generates string from party type
 * @param partyString
 * @returns string for party type (e.g. 1p, 3p)
 */
const getPartyType = (partyString: string): string => {
  switch (partyString) {
    case "FirstParty":
      return "1p";
    case "ThirdParty":
      return "3p";
    default:
      return "Unknown";
  }
};

/**
 * Checks if rightFacets contains leftFacets
 * @param leftFacets
 * @param rightFacets
 * @returns true if rightFacets contains leftFacets, false otherwise
 */
export const facetsContain = (
  leftFacets: Record<string, string[]>,
  rightFacets: Record<string, string[]>
): boolean => {
  return Object.entries(leftFacets).every(([leftKey, leftValues]) => {
    const rightValues = rightFacets[leftKey];
    if (!rightValues) {
      return false;
    }

    return leftValues.every((v) => rightValues.includes(v));
  });
};

export const isFilterEnabled = (
  filterName: FilterName,
  selectedTab: PromptTabs,
  isAgentPromptFilteringEnabled?: boolean,
  isMetaOsV2?: boolean
): boolean => {
  const AllPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
  ];

  const MicrosoftPromptsTabFilters = [
    ProductNameKey,
    DisplayCategoryKey,
    JobTypeKey,
    ExtensionKey,
    CreatedByKey,
  ];

  const CombinedPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
    CreatedByKey,
  ];
  const SearchTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    ExtensionKey,
    CreatedByKey,
    WorkgroupKey,
  ];
  const SavedPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    LikedKey,
    CreatedByKey,
    isAgentPromptFilteringEnabled ? ExtensionKey : "",
  ];
  const SavedPromptsNavItemFilters = [ProductNameKey, CreatedByKey];
  const WorkgroupPromptsTabFilters = [WorkgroupKey, CreatedByKey];
  const FromTeamsPromptsTabFilters = [
    DisplayCategoryKey,
    ProductNameKey,
    JobTypeKey,
    WorkgroupKey,
    CreatedByKey,
  ];
  const AgentPromptsTabFilters = [ExtensionKey];
  const LikedPromptsTabFilters = [
    CreatedByKey,
    ProductNameKey,
    DisplayCategoryKey,
    JobTypeKey,
    ExtensionKey,
  ];

  switch (selectedTab) {
    case PromptTabs.AllPrompts:
      return AllPromptsTabFilters.includes(filterName);
    case PromptTabs.CombinedPrompts:
      return CombinedPromptsTabFilters.includes(filterName);
    case PromptTabs.BrowsePrompts:
      return CombinedPromptsTabFilters.includes(filterName);
    case PromptTabs.WorkplacePrompts:
      return AllPromptsTabFilters.includes(filterName);
    case PromptTabs.MicrosoftPrompts:
      return MicrosoftPromptsTabFilters.includes(filterName);
    case PromptTabs.SearchPrompts:
      return SearchTabFilters.includes(filterName);
    case PromptTabs.SavedPrompts:
      return isMetaOsV2
        ? SavedPromptsNavItemFilters.includes(filterName)
        : SavedPromptsTabFilters.includes(filterName);
    case PromptTabs.GroupPrompts:
      return isMetaOsV2
        ? FromTeamsPromptsTabFilters.includes(filterName)
        : WorkgroupPromptsTabFilters.includes(filterName);
    case PromptTabs.AgentPrompts:
      return AgentPromptsTabFilters.includes(filterName);
    case PromptTabs.LikedPrompts:
      return LikedPromptsTabFilters.includes(filterName);
  }

  return false;
};
